import { useCallback, useMemo, useState } from "react";
import { TIMEFRAME_MS } from "@strat/format";
import type { ChartRangePref } from "@strat/api";

import { useUserPrefs } from "../prefs/UserPrefsProvider";

import type { ChartLayers, RangeKey } from "./Chart";

/**
 * What a reader chose to see on a bot's price chart, and the trades that
 * leaves — the state behind <ChartFilters>, shared by the browser and the
 * desktop app.
 */

type Side = "all" | "long" | "short";

/**
 * What the reader chose to see. The layers are a preference (Settings →
 * Charts) kept with the account, or with the desktop app: the same on every
 * chart, bot and visit. Which side to show is a look at one chart, so it isn't.
 */
export interface ChartView extends Required<ChartLayers> {
  side: Side;
}

/** Bars a bot's chart opens on: about this many of the most recent. */
const OPENING_BARS = 300;
const OPENING_RANGE: Array<[RangeKey, number]> = [
  ["6m", 180 * 86_400_000],
  ["3m", 90 * 86_400_000],
  ["1m", 30 * 86_400_000],
  ["1w", 7 * 86_400_000],
  ["1d", 86_400_000],
  ["1h", 3_600_000],
];

/** The preset a bot's chart opens on: the last few hundred bars, whatever the timeframe. */
export function openingRange(timeframe: string, preference: ChartRangePref = "auto"): RangeKey {
  if (preference !== "auto") return preference;
  const step = (TIMEFRAME_MS as Record<string, number>)[timeframe] ?? 3_600_000;
  return OPENING_RANGE.find(([, ms]) => ms <= OPENING_BARS * step)?.[0] ?? "1h";
}

/** `openingRange`, honouring the reader's default range (Settings → Charts). */
export function useOpeningRange(timeframe: string): RangeKey {
  return openingRange(timeframe, useUserPrefs().prefs.chart.range);
}

/** The fields the trade filters read; everything else is passed through. */
interface FilterableTrade {
  direction?: string;
}

/**
 * The reader's choices and the trades they leave. `hiddenIds` is not kept
 * across visits: indicator ids repeat from bot to bot, so hiding "sma_1" on
 * one would hide an unrelated line on the next.
 */
export function useChartView<T extends FilterableTrade>(trades: T[]) {
  const { prefs, update: updatePrefs } = useUserPrefs();
  const [side, setSide] = useState<Side>("all");
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const view: ChartView = useMemo(() => ({ ...prefs.chart.layers, side }), [prefs.chart.layers, side]);
  const update = useCallback(
    ({ side: nextSide, ...layers }: Partial<ChartView>) => {
      if (nextSide) setSide(nextSide);
      if (Object.keys(layers).length > 0) {
        updatePrefs((p) => ({ ...p, chart: { ...p.chart, layers: { ...p.chart.layers, ...layers } } }));
      }
    },
    [updatePrefs],
  );
  const toggleIndicator = (id: string) =>
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const bothSides =
    trades.some((t) => t.direction === "short") && trades.some((t) => t.direction !== "short");
  const shown = useMemo(
    () =>
      trades.filter(
        (t) => view.side === "all" || !bothSides || (t.direction === "short") === (view.side === "short"),
      ),
    [trades, view.side, bothSides],
  );

  return { view, update, hiddenIds, toggleIndicator, shown, bothSides, total: trades.length };
}

/** What the trade markers mean — the part of the key every price chart shares. */
export const CHART_MARKERS_KEY = "▲ entry · ▼ exit (hover one for the side, price and result)";

/** The key under a bot chart: what each marker and line means. */
export const CHART_KEY = `${CHART_MARKERS_KEY} · dashed line: open position · dotted red line: stop-loss.`;
